import { useEffect, useRef, useState } from "react";
import resolveAudioUrl from "../../../utils/audio/resolveAudioUrl";
import { issueAuthoringPreviewMediaDescriptor } from "../services/activityAuthoringApi";
import { issueAssignmentInspectionMediaDescriptor } from "../../assignments/services/assignmentApi";

const REFRESH_LEAD_MS = 15000;
const protectedDescriptorCache = new Map();

export function clearProtectedPreviewDescriptorCache() {
  protectedDescriptorCache.clear();
}

const AUTHORING_CONTEXT = "FACILITATOR_AUTHORING_PREVIEW";
const ASSIGNMENT_CONTEXT = "ASSIGNMENT_ACTIVITY_INSPECTION";

async function issueDescriptor({ consumerContext, assignmentId, stimulusInstanceId, activityId }) {
  if (consumerContext === AUTHORING_CONTEXT) return issueAuthoringPreviewMediaDescriptor({ stimulusInstanceId, activityId });
  if (consumerContext === ASSIGNMENT_CONTEXT && assignmentId) {
    return issueAssignmentInspectionMediaDescriptor({ assignmentId, stimulusInstanceId, activityId });
  }
  throw new Error("PROTECTED_MEDIA_CONSUMER_CONTEXT_INVALID");
}

async function acquireProtectedDescriptor({ consumerContext, assignmentId, stimulusInstanceId, activityId, force = false }) {
  const key = `${consumerContext}:${assignmentId || "none"}:${activityId}:${stimulusInstanceId}`;
  const cached = protectedDescriptorCache.get(key);
  if (!force && cached?.descriptor && validateProtectedPreviewDescriptor(cached.descriptor, stimulusInstanceId, Date.now(), consumerContext)) return cached.descriptor;
  if (!force && cached?.promise) return cached.promise;
  const promise = issueDescriptor({ consumerContext, assignmentId, stimulusInstanceId, activityId })
    .then(descriptor => {
      if (!validateProtectedPreviewDescriptor(descriptor, stimulusInstanceId, Date.now(), consumerContext)) throw new Error("PROTECTED_MEDIA_DESCRIPTOR_INVALID");
      protectedDescriptorCache.set(key, { descriptor });
      return descriptor;
    })
    .catch(error => { protectedDescriptorCache.delete(key); throw error; });
  protectedDescriptorCache.set(key, { promise });
  return promise;
}

export function validateProtectedPreviewDescriptor(descriptor, stimulusInstanceId, evaluationTime = Date.now(), consumerContext = AUTHORING_CONTEXT) {
  return Boolean(descriptor)
    && descriptor.descriptorVersion === "authorized_media_descriptor.v1"
    && descriptor.mediaRef?.type === "stimulusInstance"
    && descriptor.mediaRef?.canonicalId === `stimulus-instance:${stimulusInstanceId}`
    && descriptor.consumerContext === consumerContext
    && descriptor.transportKind === "PRESIGNED_PRIVATE_OBJECT"
    && descriptor.refreshMode === "REAUTHORIZE_SAME_MEDIA"
    && descriptor.contentType === "audio/wav"
    && descriptor.byteLength === 2281572
    && typeof descriptor.playbackUrl === "string"
    && /^https:\/\//.test(descriptor.playbackUrl)
    && Number.isFinite(Date.parse(descriptor.expiresAt))
    && Date.parse(descriptor.expiresAt) > evaluationTime;
}

function protectedPreviewError(error) {
  const code = error?.response?.data?.error || error?.message;
  if (code === "AUTHENTICATION_REQUIRED") return "Sign in again to preview this protected sample.";
  if (code === "CONSUMER_AUTHORIZATION_DENIED") return "You are not authorized to preview this sample.";
  if (code === "MEDIA_NOT_IN_CONSUMER_CONTEXT") return "This sample is not available in the current authoring context.";
  if (["EXACT_MEDIA_NOT_FOUND", "EXACT_MEDIA_METADATA_UNAVAILABLE"].includes(code)) return "Protected media is unavailable.";
  return "Protected preview could not be prepared.";
}

export function authoringPreviewUrl(contentRef) {
  // VoiceSample/StimulusInstance media references use the same root-relative
  // public-sample convention as the established participant runtimes.
  return resolveAudioUrl(contentRef);
}

export default function SamplePreview({ presentation, label, previewId, activePreview, onPreviewPlay, stimulusInstanceId, activityId = "new-personal", protectedMediaContext = null }) {
  const contentRef = presentation?.preview?.contentRef;
  const protectedPilot = presentation?.preview?.protectedMediaPilot === true;
  const consumerContext = protectedMediaContext?.consumerContext || AUTHORING_CONTEXT;
  const assignmentId = protectedMediaContext?.assignmentId || null;
  const audioRef = useRef(null);
  const [state, setState] = useState("idle");
  const [protectedSource, setProtectedSource] = useState(null);
  const [protectedError, setProtectedError] = useState("");
  useEffect(() => setState("idle"), [contentRef]);
  useEffect(() => {
    if (previewId && activePreview && activePreview !== previewId) audioRef.current?.pause();
  }, [activePreview, previewId]);
  useEffect(() => {
    let active = true;
    let refreshTimer;
    if (!protectedPilot) {
      setProtectedSource(null);
      setProtectedError("");
      return () => { active = false; };
    }
    async function acquire(force = false) {
      setState("loading");
      setProtectedError("");
      try {
        const descriptor = await acquireProtectedDescriptor({ consumerContext, assignmentId, stimulusInstanceId, activityId, force });
        if (!active) return;
        setProtectedSource(descriptor.playbackUrl);
        setState("ready");
        const refreshDelay = Math.max(0, Date.parse(descriptor.expiresAt) - Date.now() - REFRESH_LEAD_MS);
        refreshTimer = window.setTimeout(() => acquire(true), Math.min(refreshDelay, 2147483647));
      } catch (error) {
        if (!active) return;
        setProtectedSource(null);
        setProtectedError(protectedPreviewError(error));
        setState("error");
      }
    }
    if (typeof stimulusInstanceId === "string" && stimulusInstanceId) acquire();
    else { setProtectedError("Protected preview could not be prepared."); setState("error"); }
    return () => { active = false; window.clearTimeout(refreshTimer); };
  }, [protectedPilot, consumerContext, assignmentId, stimulusInstanceId, activityId]);

  if (!presentation?.preview?.available || !presentation.preview.contentRef) {
    return <span className="av-authoring-sample__unavailable">Preview unavailable</span>;
  }
  return (
    <div className="av-authoring-sample__preview">
      <audio
        ref={audioRef}
        aria-label={`Preview ${label || "selected sample"}`}
        controls
        controlsList="nodownload"
        onCanPlay={() => setState("ready")}
        onError={() => setState("error")}
        onLoadStart={() => setState("loading")}
        onPlay={() => onPreviewPlay?.(previewId)}
        preload="none"
        src={protectedPilot ? protectedSource || undefined : authoringPreviewUrl(contentRef)}
        data-protected-media={protectedPilot ? "pilot" : undefined}
      />
      <span aria-live="polite" className="av-authoring-sample__preview-status">
        {state === "loading" ? (protectedPilot ? "Preparing protected preview…" : "Loading preview…") : state === "error" ? (protectedError || "Preview could not be loaded.") : ""}
      </span>
    </div>
  );
}
